import { io } from 'socket.io-client';

import type { MessagePayload } from '../types/ChatWindow';
import type { IOBundle, IOEvent } from '../types/io';
import { getUserId } from './localStorageService';
import { sendLogMessage } from './postMessageService';

type IO = ReturnType<typeof io>;

let IOInstance: IO | null = null;
let initialUri: string;
let initialBotId: string;
let connected = false;
let hasBeenDisconnected = false;
// Tracks whether the current disconnection was caused by a real browser-level
// network outage (offline event), as opposed to a server-side disconnect (e.g.
// the server closing the socket when another tab connects with the same user ID).
// Only real outages should trigger the offline banner and the "restored" banner.
let hadNetworkOutage = false;
// Stored so handleOffline can call it directly when the socket is already down.
let connectionStatusCallback: ((status: boolean) => void) | undefined;

const ioConfig = {
  path: '/sockets',
  transports: ['websocket'],
};

// Browser offline event handlers
const handleOffline = () => {
  hadNetworkOutage = true;

  if (IOInstance && connected && !hasBeenDisconnected) {
    // Socket is currently connected; disconnect it — the disconnect handler
    // will call connectionStatusCallback(false) once it fires.
    IOInstance?.disconnect();
  } else {
    // Socket is already disconnected (e.g. server kicked it earlier).
    // The disconnect handler won't fire again, so call the callback directly.
    connectionStatusCallback?.(false);
  }
};

// Browser online event handlers
const handleOnline = () => {
  // Attempt to reconnect if the socket is not connected
  if (IOInstance && !connected) {
    IOInstance.connect();
  }
};

const addBrowserEventListeners = () => {
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);
};

const removeBrowserEventListeners = () => {
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('online', handleOnline);
};

const init = async (
  uri: string,
  botId: string,
  configId: string,
  onRegisterEvent: () => void,
  onConnectionStatusChangeCallback?: (boolean) => void,
  onConnected?: () => void,
) => {
  initialUri = uri;
  initialBotId = botId;
  // Keep a module-level reference so handleOffline can reach it without a closure.
  connectionStatusCallback = onConnectionStatusChangeCallback;

  // Add browser event listeners
  addBrowserEventListeners();

  // If the browser is already offline when init is called (e.g. the user clicked
  // "Let's go!" while the internet was already disabled), the 'offline' event
  // fired before connectionStatusCallback was set, so it was never invoked.
  // Trigger the offline state now so the banner and disabled input appear.
  if (!navigator.onLine) {
    hadNetworkOutage = true;
    hasBeenDisconnected = true;
    connectionStatusCallback?.(false);
  }

  if (!IOInstance) {
    console.debug(`Initializing IO service for ${botId}`);
    IOInstance = io(`${uri}/web-client`, {
      query: {
        id: getUserId(configId),
        'conversed-bot': botId,
      },
      ...ioConfig,
    });

    // Wait for the socket to connect and get the id
    IOInstance.on('connect', async () => {
      const id = await getUserId(configId);
      if (id) {
        send({
          event: 'id',
          data: { id, type: 'id', newId: { id: getSocketID() } },
        });
      }

      // Only show the "connection restored" banner if the disconnection was
      // caused by a real network outage — not by a server-side socket close.
      if (hasBeenDisconnected && hadNetworkOutage) {
        onConnectionStatusChangeCallback?.(true);
        hadNetworkOutage = false;
      }

      hasBeenDisconnected = false;

      sendLogMessage(
        `Websocket ${IOInstance?.recovered ? 'reconnected' : 'connected'}`,
        'info',
      );

      connected = true;
      onRegisterEvent();
      // Notify the caller that the socket is fully connected and events are registered.
      // Used by reInitialize callers that need to re-send state (e.g. userStatus) on
      // the new socket after the old one was torn down.
      onConnected?.();
    });

    IOInstance.on('disconnect', async () => {
      sendLogMessage('Websocket disconnected', 'error');
      connected = false;
      hasBeenDisconnected = true;
      // Only show the offline banner when the browser itself reports a network
      // outage. Server-side disconnects (e.g. another tab connecting with the
      // same user ID) must not trigger a false offline message.
      if (hadNetworkOutage) {
        onConnectionStatusChangeCallback?.(false);
      }
    });
  } else {
    // If the websocket connection is already established, just register the events again ( example: after closing and re-opening the chat window )
    onRegisterEvent();
    onConnected?.();
  }
};

const registerEvent = ({ event, callback }: IOEvent) => {
  IOInstance?.on(event, (payload: MessagePayload) => {
    const direction = payload?.type === 'system' ? 'system' : 'out';
    callback(direction, payload);
  });
};

const send = ({ event, data }: IOBundle) => {
  IOInstance?.emit(event, data);
};

const disconnect = () => {
  IOInstance?.disconnect();
  removeBrowserEventListeners(); // Clean up browser event listeners
};

const reInitialize = (
  configId: string,
  onRegisterEvent: () => void,
  onConnectionStatusChangeCallback?: (status: boolean) => void,
  onConnected?: () => void,
) => {
  disconnect();
  IOInstance = null;

  if (navigator.onLine) {
    // Online re-initialization (e.g. "Continue here" with a live connection):
    // reset both flags so the new socket does not show a false "restored" banner.
    hasBeenDisconnected = false;
    hadNetworkOutage = false;
  } else {
    // Offline re-initialization: ensure both flags are set so that when the
    // socket eventually connects after the network returns, the connect handler
    // correctly shows the "connection restored" banner.
    hasBeenDisconnected = true;
    hadNetworkOutage = true;
  }

  init(
    initialUri,
    initialBotId,
    configId,
    onRegisterEvent,
    onConnectionStatusChangeCallback,
    onConnected,
  );
};

// Return whether the socket is currently connected
const isConnected = () => {
  return connected;
};

export function getSocketID() {
  return IOInstance?.id;
}

export default {
  init,
  reInitialize,
  registerEvent,
  send,
  disconnect,
  isConnected,
};
